import { WebCodecsPlayerChunk } from './webcodecs-player';

export type RendererDrawFunc =  (frame: VideoFrame) => void;

export enum WebCodecsVideoDecSupport {
  AVC1 = 1,
}

/**
 * @param avcCBoxData expects mp4 box raw content without ISO-FF size/type 8-bytes ahead
 */
function getAvcDecoderConfig(avcCBoxData: Uint8Array): VideoDecoderConfig {
  /*
    avc1[.PPCCLL] (AVC video)
    PPCCLL are six hexadecimal digits specifying
    the profile number (PP), constraint set flags (CC), and level (LL).
    See AVC profiles for the possible values of PP.
  */
  const codecProfile: number = (avcCBoxData[1] << 16) | (avcCBoxData[2] << 8) | avcCBoxData[3];
  // avc1.XXYYZZ -- XX - profile + YY - constraints + ZZ - level
  const codecString: string = 'avc1.' + (0x1000000 | codecProfile).toString(16).substring(1)
  const decoderConfig: VideoDecoderConfig = {
    codec: codecString, // e.g 'avc1.42c01e',
    description: avcCBoxData
  }
  return decoderConfig;
}

export class WebCodecsVideoDecHandler {

  private _isConfigured = false;
  private _gotFirstKeyFrame = false;
  private _decoder: VideoDecoder;

  private _currentTimeUs = NaN;

  constructor(private _drawImage: RendererDrawFunc, codecSupport: WebCodecsVideoDecSupport) {
    if (!(codecSupport === WebCodecsVideoDecSupport.AVC1)) {
      throw new Error('No support for codec type: ' + codecSupport);
    }
    this._decoder = new VideoDecoder({
      output: this._onVideoDecFrameOut.bind(this),
      error: this._onVideoDecError.bind(this)
    });
  }

  get currentTimeUs(): number {
    return this._currentTimeUs;
  }

  decode(chunk: WebCodecsPlayerChunk) {

    const {
      payloadUnit,
      dtsUs,
      ptoUs,
      isCodecConfig,
      isKeyframe
    } = chunk;

    if (isCodecConfig) {
      if (this._isConfigured) return;

      const decoderConfig = getAvcDecoderConfig(payloadUnit);

      //console.log('codec config:', videoDecoderConfig)

      if (!VideoDecoder.isConfigSupported(decoderConfig)) {
        throw new Error(`VideoDec config not supported: ${JSON.stringify(decoderConfig)}`);
      }

      this._decoder.configure(decoderConfig);
      if(this._decoder.state !== 'configured') {
        throw new Error(`VideoDecoder configure failed with param: ${JSON.stringify(decoderConfig)}`);
      }
      this._isConfigured = true;

      console.log('video codec configure done, state:', this._decoder.state, decoderConfig);

      return;
    }
    if (!this._isConfigured) return;

    if (!this._gotFirstKeyFrame) {
      if (!isKeyframe) return;
      this._gotFirstKeyFrame = true;
    }

    // expecting any frame nalus from here

    // todo: handle PTO / reordering

    const vidChunk = new EncodedVideoChunk({
      type: isKeyframe ? 'key' : 'delta',
      timestamp: dtsUs, // cast from bigint
      data: payloadUnit
    });

    //console.log('push to decode:', vidChunk, 'state:', videoDec.state);
    this._decoder.decode(vidChunk)
  }

  /**
   * Transfers VideoFrame ownership to renderer
   */
  private _onVideoDecFrameOut = (frame: VideoFrame) => {

    this._currentTimeUs = frame.timestamp;
    this._drawImage(frame);
  }

  private _onVideoDecError = (err: Error) => {
    console.error('VideoDecoder error:', err);
  }

}
